/**
 * Wide mega-menu interaction for Shop global nav.
 * Mirrors Astro WideNavController: open on pointerover, ~180ms leave grace,
 * backdrop dismiss, Escape, outside pointer. Click on primary follows href
 * (theme.js toggle is suppressed). Tagular stays in global-shop-nav.js.
 */
(function () {
  const DESKTOP_MQ = '(min-width: 1200px)';
  const LEAVE_GRACE_MS = 180;

  const isDesktop = () => window.matchMedia(DESKTOP_MQ).matches;

  let closeTimer = null;
  let activeMenuId = null;
  let panelHasFocus = false;

  const activeTabByMenu = new Map();

  function getHeader() {
    return document.querySelector('store-header.global-header');
  }

  function getBackdrop() {
    return document.querySelector('[data-mega-dismiss]');
  }

  function setOverlayTop() {
    const header = getHeader();
    if (!header) return;
    const top = Math.round(header.getBoundingClientRect().bottom);
    document.documentElement.style.setProperty('--mega-menu-top', top + 'px');
    document.documentElement.style.setProperty('--nav-overlay-top', top + 'px');
  }

  function clearCloseTimer() {
    if (closeTimer) {
      clearTimeout(closeTimer);
      closeTimer = null;
    }
  }

  function getWrap(menuEl) {
    return menuEl.querySelector('.mega-menu__inner');
  }

  function getFirstBtn(wrap) {
    return wrap && wrap.querySelector('.mega-tab-btn[data-tab-id]');
  }

  function getKeyFromBtn(btn) {
    return (
      (btn && (btn.dataset.tabKey || (btn.dataset.tabId || '').replace(/^tab-/, ''))) ||
      ''
    );
  }

  function activateTab(wrap, key) {
    if (!wrap || !key) return false;
    const btn = wrap.querySelector(
      '.mega-tab-btn[data-tab-key="' + key + '"],.mega-tab-btn[data-tab-id="tab-' + key + '"]'
    );
    const panel = wrap.querySelector('.tab-panel[data-tab-key="' + key + '"]');
    if (!btn || !panel) return false;
    wrap.querySelectorAll('.mega-tab-btn.is-active').forEach(function (b) {
      b.classList.remove('is-active');
      b.setAttribute('aria-selected', 'false');
    });
    wrap.querySelectorAll('.tab-panel.active').forEach(function (p) {
      p.classList.remove('active');
    });
    btn.classList.add('is-active');
    btn.setAttribute('aria-selected', 'true');
    panel.classList.add('active');
    return true;
  }

  function bindTabs(menuEl) {
    const wrap = getWrap(menuEl);
    if (!wrap || menuEl.__tabsBound) return;
    wrap.addEventListener('click', function (e) {
      const btn = e.target.closest('.mega-tab-btn[data-tab-id]');
      if (!btn || !wrap.contains(btn)) return;
      const key = getKeyFromBtn(btn);
      if (activateTab(wrap, key)) activeTabByMenu.set(menuEl.id, key);
    });
    const firstKey = getKeyFromBtn(getFirstBtn(wrap));
    if (firstKey) {
      activateTab(wrap, firstKey);
      activeTabByMenu.set(menuEl.id, firstKey);
    }
    menuEl.__tabsBound = true;
  }

  function syncBackdrop() {
    const backdrop = getBackdrop();
    const anyOpen = !!document.querySelector(
      '.header.global-header .mega-menu:not([hidden])'
    );
    if (backdrop) {
      backdrop.hidden = !anyOpen;
      backdrop.setAttribute('aria-hidden', anyOpen ? 'false' : 'true');
    }
    document.documentElement.classList.toggle('lp-mega-open', anyOpen);
  }

  function closeAll() {
    document.querySelectorAll('.header.global-header .mega-menu').forEach(function (mm) {
      if (!mm.hasAttribute('hidden')) {
        mm.setAttribute('hidden', '');
        const t = document.querySelector('[aria-controls="' + mm.id + '"]');
        if (t) t.setAttribute('aria-expanded', 'false');
      }
    });
    activeMenuId = null;
    panelHasFocus = false;
    syncBackdrop();
  }

  function openMenu(trigger, menuEl) {
    if (!isDesktop() || !menuEl || !trigger) return;
    clearCloseTimer();
    setOverlayTop();

    if (activeMenuId && activeMenuId !== menuEl.id) {
      const prev = document.getElementById(activeMenuId);
      if (prev) {
        prev.setAttribute('hidden', '');
        const prevTrigger = document.querySelector('[aria-controls="' + activeMenuId + '"]');
        if (prevTrigger) prevTrigger.setAttribute('aria-expanded', 'false');
      }
    }

    menuEl.removeAttribute('hidden');
    trigger.setAttribute('aria-expanded', 'true');
    activeMenuId = menuEl.id;
    panelHasFocus = false;

    bindTabs(menuEl);
    const wrap = getWrap(menuEl);
    const savedKey = activeTabByMenu.get(menuEl.id);
    if (!menuEl.__everOpened) {
      const firstKey = getKeyFromBtn(getFirstBtn(wrap));
      if (firstKey) {
        activateTab(wrap, firstKey);
        activeTabByMenu.set(menuEl.id, firstKey);
      }
      menuEl.__everOpened = true;
    } else if (savedKey) {
      activateTab(wrap, savedKey);
    } else {
      const firstKey = getKeyFromBtn(getFirstBtn(wrap));
      if (firstKey) {
        activateTab(wrap, firstKey);
        activeTabByMenu.set(menuEl.id, firstKey);
      }
    }

    syncBackdrop();
  }

  function scheduleClose() {
    if (panelHasFocus) return;
    clearCloseTimer();
    closeTimer = setTimeout(function () {
      if (!isDesktop()) return;
      closeAll();
    }, LEAVE_GRACE_MS);
  }

  function panelContains(target) {
    if (!(target instanceof Element) || !activeMenuId) return false;
    const menuEl = document.getElementById(activeMenuId);
    if (!menuEl) return false;
    if (menuEl.contains(target)) return true;
    const trigger = document.querySelector('[aria-controls="' + activeMenuId + '"]');
    return !!(trigger && (trigger === target || trigger.contains(target)));
  }

  function onPointerOver(e) {
    if (!isDesktop() || e.pointerType === 'touch') return;
    const target = e.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-mega-dismiss]')) {
      scheduleClose();
      return;
    }

    const trigger = target.closest(
      '.header.global-header .header__linklist-item.has-dropdown > a[aria-controls]'
    );
    if (trigger) {
      const menuEl = document.getElementById(trigger.getAttribute('aria-controls'));
      if (menuEl) openMenu(trigger, menuEl);
      return;
    }

    if (panelContains(target)) {
      clearCloseTimer();
      return;
    }

    const primaryLink = target.closest(
      '.header.global-header .header__linklist-item > a[data-topnav="true"]'
    );
    if (primaryLink && !primaryLink.hasAttribute('aria-controls')) {
      scheduleClose();
    }
  }

  function onPointerLeaveNav() {
    if (!isDesktop()) return;
    scheduleClose();
  }

  function init() {
    const header = getHeader();
    if (!header || header.__lpMegaInit) return;
    header.__lpMegaInit = true;

    setOverlayTop();
    window.addEventListener('resize', setOverlayTop);
    window.addEventListener('scroll', setOverlayTop, { passive: true });

    document.querySelectorAll('.header.global-header .mega-menu').forEach(bindTabs);

    header.addEventListener('pointerover', onPointerOver);
    header.addEventListener('pointerleave', onPointerLeaveNav);

    header.addEventListener('focusin', function (e) {
      if (!isDesktop()) return;
      const trigger = e.target.closest(
        '.header__linklist-item.has-dropdown > a[aria-controls]'
      );
      if (trigger) {
        const menuEl = document.getElementById(trigger.getAttribute('aria-controls'));
        if (menuEl) {
          openMenu(trigger, menuEl);
          panelHasFocus = true;
        }
        return;
      }
      if (activeMenuId && panelContains(e.target)) {
        clearCloseTimer();
        panelHasFocus = true;
      }
    });

    header.addEventListener('focusout', function (e) {
      if (!isDesktop()) return;
      const to = e.relatedTarget;
      if (to instanceof Node && panelContains(to)) return;
      if (to instanceof Node && header.contains(to)) {
        panelHasFocus = false;
        return;
      }
      panelHasFocus = false;
      scheduleClose();
    });

    /*
      Suppress Focal DesktopNavigation click-toggle so primary links navigate
      like .com (href / data-nav-url). Hover owns open/close.
    */
    header.addEventListener(
      'click',
      function (e) {
        if (!isDesktop()) return;
        const trigger = e.target.closest(
          '.header__linklist-item.has-dropdown > a[aria-controls]'
        );
        if (!trigger) return;
        e.stopImmediatePropagation();
      },
      true
    );

    document.addEventListener('pointerdown', function (e) {
      if (!isDesktop() || !activeMenuId) return;
      const t = e.target;
      if (!(t instanceof Node)) return;
      if (panelContains(t)) return;
      if (t instanceof Element && t.closest('[data-mega-dismiss]')) {
        closeAll();
        return;
      }
      const headerEl = getHeader();
      if (headerEl && headerEl.contains(t)) {
        if (
          t instanceof Element &&
          !t.closest('.global-menu-design .has-dropdown, .mega-menu')
        ) {
          closeAll();
        }
        return;
      }
      closeAll();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && activeMenuId) {
        e.preventDefault();
        const trigger = document.querySelector('[aria-controls="' + activeMenuId + '"]');
        closeAll();
        if (trigger) trigger.focus();
      }
    });

    const backdrop = getBackdrop();
    if (backdrop) {
      backdrop.addEventListener('click', function () {
        closeAll();
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
